import { useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Button, Card, CardContent, CircularProgress, Stack, Typography } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import { generatePaymentDocuments } from "../../../../services/backend/PaymentRequestLegalService";
import { formatReviewMoment } from "./paymentReviewUi";

// Orden de presentación de los documentos del juego (mismas claves que el
// catálogo del backend, supervisionReport/documents.js). Un documento que el
// backend agregue después aparece al final con su propia etiqueta.
const DOCUMENT_ORDER = ["supervisionReport", "receiptCertificate"];

// Documentos de supervisión de la solicitud (legal-payment-order): Informe
// FO-GBS-01 y Acta de recibo a satisfacción FO-GAD-10.
//
// El backend los genera juntos al aprobar; aquí se descarga cada uno de la
// versión más reciente y, para quien puede (`canRegenerate`: supervisor
// efectivo o ADMIN), se regenera el juego. Si la última generación falló se
// dice; a quien no puede regenerar se le muestra solo que aún no hay documentos.
const PaymentDocumentsCard = ({ request, canRegenerate = false, onUpdated, onError }) => {
  const user = useSelector((state) => state.user.value);
  const [busy, setBusy] = useState(false);

  const set = request?.paymentDocuments || null;
  const failure = request?.paymentDocumentsError || null;
  const documents = set?.documents || {};
  const keys = [
    ...DOCUMENT_ORDER.filter((key) => documents[key]),
    ...Object.keys(documents).filter((key) => !DOCUMENT_ORDER.includes(key)),
  ];

  const handleRegenerate = async () => {
    setBusy(true);
    const result = await generatePaymentDocuments(request.requestId, user);
    setBusy(false);
    if (!result.ok) {
      if (onError) onError(result.message || "No se pudieron generar los documentos de supervisión");
      return;
    }
    if (onUpdated) onUpdated(result.data);
  };

  return (
    <Card variant="outlined" sx={{ my: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Documentos de supervisión
        </Typography>

        {set ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Versión {set.version} · generados el {formatReviewMoment(set.generatedAt)}
            {set.generatedByName ? ` por ${set.generatedByName}` : ""}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Esta solicitud todavía no tiene documentos de supervisión.
          </Typography>
        )}

        {failure && canRegenerate && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            La última generación de los documentos falló ({formatReviewMoment(failure.at)}): {failure.message}
          </Alert>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
          {keys.map((key) => (
            <Button
              key={key}
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              href={documents[key].url}
              target="_blank"
              rel="noopener noreferrer"
              title={documents[key].formatVersion}
            >
              {documents[key].label || key}
            </Button>
          ))}
          {canRegenerate && (
            <Button
              variant="outlined"
              startIcon={busy ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRegenerate}
              disabled={busy}
            >
              {set ? "Regenerar" : "Generar documentos"}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PaymentDocumentsCard;
